/* eslint-disable prefer-const */
import { Blok } from 'components/Page';
import { Datasources } from 'context/DataSourceContext';

import { Product } from '@savebetter/components';

// shallow lookup regexp for '{<alphanumeric-dash-underscore>}' pattern
// {1-APY} is the shortest var we have for now
export const varRegExp = new RegExp(/{([\w-]){5,40}}/, 'g');

// lookup regexp for '{todayDate}' pattern
const todayVarRegExp = new RegExp(/{todayDate}/, 'g');

// lookup regexp for '{currentMonth}' pattern
const currentMonthVarRegExp = new RegExp(/{currentMonth}/, 'g');

// lookup regexp for '{<letters|numbers>-APY}' pattern
const idApyVarRegExp = new RegExp(/{([A-z0-9]){4,30}(-APY){1}}/, 'g');

// lookup regexp for '{<letters|numbers>-product}' pattern
export const productIdVarRegExp = new RegExp(/{([A-z0-9]){4,30}(-product){1}}/, 'g');

// lookup regexp for '{<FDIC|NCUA>-<alphanumeric-dash-underscore>}' pattern
export const nationalAvgVarRegExp = new RegExp(/{(FDIC|NCUA)-{1}([A-Z0-9-_]){1,10}}/, 'g');

// lookup regexp for {<letters|numbers>-APY}{<FDIC|NCUA>-<rest>} vars combined pattern
const apyWithNationalAvgVarRegExp = new RegExp(/{([A-z0-9]){4,30}(-APY){1}}{(FDIC|NCUA)-{1}([A-Z0-9-_]){1,10}}/, 'g');

const interestRateRegExp = new RegExp(/\bshortcode\w*\b/, 'g');

const DEFAULT_VALUE = '--';

/*
  replace {todayDate} in en-us long-style format
  es. {todayDate} => January 1, 2020
*/
const replaceTodayVar = (text: string): string => {
  const todayDate = new Date().toLocaleDateString('en-US', { dateStyle: 'long' });
  return text.replaceAll(todayVarRegExp, todayDate);
};

/*
  replace {currentMonth} in en-us long-style format
  ex. {currentMonth} => January
*/
const replaceCurrentMonthVar = (text: string): string => {
  const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long' });
  return text.replaceAll(currentMonthVarRegExp, currentMonth);
};

// return variable without curly-braces {xyz-APY} => xyz-APY
const getVariableName = (variable: string) => variable.replace(/[{}]/g, '');

/* return variable without curly-braces and anything after the   first - sign: {xyz-APY} => xyz || {xyz-product} => xyz
 */
export const getProductIdFromVariable = (variable: string) => variable.replace(/[{}]/g, '').split('-')[0];

// replace {productId-APY} custom var with the actual product apy value
const replaceIdApyVars = (text: string, products: Product[] | null) => {
  const customVarIds = text.match(idApyVarRegExp);

  if (customVarIds && products) {
    for (const customVarId of customVarIds) {
      // removing curly braces from customVarId and gather productId
      const productId = getProductIdFromVariable(customVarId);
      //find <productId>-APY variable's product by id
      const product = products?.find(({ id }) => id === productId);
      const productAPYStr = product ? `${product.annualPercentageYield}` : `${DEFAULT_VALUE}%`;

      return text.replaceAll(customVarId, productAPYStr);
    }
  }
  return text;
};

// replace {shortcodeXYZ} custom var with the actual interest rate shortcode
const replaceInterestRateVars = (text: string, datasources: Datasources) => {
  // isolate all interest rate shortcodes vars
  const customVarAvgs = text.match(interestRateRegExp);

  if (customVarAvgs && datasources) {
    for (const customVarAvg of customVarAvgs) {
      const customVarAvgName = getVariableName(customVarAvg);
      // select custom var's datasource
      const datasourceName = 'interest_rate_shortcodes';
      // find node which contains interest rate shortcodes custom var's value
      const datasourceNode = datasources[datasourceName].edges.find(({ node }) => node.name === customVarAvgName);
      // value from existing datasource node or --, split fixes an issue with values containing "/t" suffix
      const replaceValue = datasourceNode ? datasourceNode.node.value.split('/')[0] : DEFAULT_VALUE;

      return text.replaceAll(`{${customVarAvg}}`, replaceValue);
    }
  }
  return text;
};

// replace national average custom var with the actual national average value: {FDIC-S} => 0.09
export const replaceAverageVars = (text: string, datasources: Datasources | null): string => {
  // isolate all national average vars
  const customVarAvgs = text.match(nationalAvgVarRegExp);

  if (customVarAvgs && datasources) {
    for (const customVarAvg of customVarAvgs) {
      const customVarAvgName = getVariableName(customVarAvg);
      // select custom var's datasource
      const datasourceName = customVarAvgName.slice(0, 4).toLowerCase();
      // find node which contains national avg custom var's value
      const datasourceNode = datasources[datasourceName].edges.find(({ node }) => node.name === customVarAvgName);
      // value from existing datasource node or --, split fixes an issue with values containing "/t" suffix
      const replaceValue = datasourceNode ? datasourceNode.node.value.split('/')[0] : DEFAULT_VALUE;

      return text.replaceAll(customVarAvg, replaceValue);
    }
  }
  return text;
};

/* replace coupled product-id-apy and national average custom vars with calculated comparison between the two. Ex:
{productId-APY}{FDIC-60-M-CD} => 21.45x
 */
export const replaceComparedApyWithAvgVars = (text: string, products: Product[], datasources: Datasources): string => {
  // coupled vars {<letters|numbers>-APY}{<FDIC|NCUA>-<rest>}
  const coupledVars = text.match(apyWithNationalAvgVarRegExp);
  if (coupledVars) {
    for (const coupledVar of coupledVars) {
      // extract productId and nationalAvgName from a coupled var
      let [productId, nationalAvgName] = coupledVar.split(/[{}]/).filter(Boolean);
      //finds xyz123-APY custom variable's product
      productId = productId.split('-')[0];
      const product = products?.find(({ id }) => id === productId);
      // select custom var's datasource
      const datasourceName = nationalAvgName.slice(0, 4).toLowerCase();
      // find node which contains national avg custom var's value
      const datasourceNode = datasources[datasourceName].edges.find(({ node }) => node.name === nationalAvgName);
      // value from existing datasource node or --,split fixes an issue with values containing "/t" suffix
      let replaceValue = DEFAULT_VALUE;
      if (product && datasourceNode) {
        // compare a product Apy to National Average returning how many times is higher than national average
        const comparedNumber = parseFloat(product.annualPercentageYield) / parseFloat(datasourceNode.node.value);

        replaceValue = comparedNumber.toFixed(1);
      }
      return text.replaceAll(coupledVar, `${replaceValue}x`);
    }
  }
  return text;
};

export const replaceCustomVars = (
  content: string | HTMLElement,
  products: Product[] | null = null,
  datasources: Datasources | null = null
): string => {
  let text = typeof content === 'string' ? content : content.innerHTML;
  const customCoupledVars = text.match(apyWithNationalAvgVarRegExp);
  // get all custom variables in the given string
  const customVars = text.match(varRegExp);

  if (customCoupledVars && products && datasources) {
    // checks if string has coupled apy and nationalAvg custom vars
    text = replaceComparedApyWithAvgVars(text, products, datasources);
  }
  if (customVars) {
    for (const customVar of customVars) {
      // check if string has today custom var
      if (todayVarRegExp.test(customVar)) {
        text = replaceTodayVar(text);
      }
      if (currentMonthVarRegExp.test(customVar)) {
        text = replaceCurrentMonthVar(text);
      }
      // check if string has apy custom var
      else if (idApyVarRegExp.test(customVar) && products) {
        text = replaceIdApyVars(text, products);
      }
      // check if string has FDIC or NCUA custom var
      else if (nationalAvgVarRegExp.test(text) && datasources) {
        text = replaceAverageVars(text, datasources);
      }
      // check if string has interest rate shortcode
      else if (interestRateRegExp.test(text) && products && datasources) {
        text = replaceInterestRateVars(text, datasources);
      }
    }
  }
  return text;
};

export const replaceCustomVarsInBlok = (
  blok: Blok,
  products: Product[] | null = null,
  datasources: Datasources | null = null
): Blok => {
  const blokToString = JSON.stringify(blok);
  return varRegExp.test(blokToString) ? JSON.parse(replaceCustomVars(blokToString, products, datasources)) : blok;
};
